import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Search, Clock, BookOpen, Target, Grid, List, ChevronRight, Play, ClipboardCheck } from 'lucide-react'
import Navigation from '../../components/common/Navigation'
import Footer from '../../components/common/Footer'
import { quizBankService, QuizBankItem } from '../../services/quiz/QuizBankService'
import { textToSpeechService } from '../../services/tts/textToSpeechService'

const TracNghiem: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const [availableQuizzes, setAvailableQuizzes] = useState<QuizBankItem[]>([])
  const [filteredQuizzes, setFilteredQuizzes] = useState<QuizBankItem[]>([])
  const [loading, setLoading] = useState(true)
  const [scanStatus, setScanStatus] = useState<string>('Vui lòng chờ trong giây lát')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedLoai, setSelectedLoai] = useState<string>(searchParams.get('loai') || 'all')
  const [selectedLop, setSelectedLop] = useState<string>(searchParams.get('lop') || 'all')
  const [selectedBai, setSelectedBai] = useState<string>(searchParams.get('bai') || 'all')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')

  // Lấy danh sách bài học theo lớp
  const getBaiListByClass = (lop: string): number[] => {
    switch (lop) {
      case '10':
        return Array.from({ length: 20 }, (_, i) => i + 1)
      case '11':
        return Array.from({ length: 18 }, (_, i) => i + 1)
      case '12':
        return Array.from({ length: 22 }, (_, i) => i + 1)
      default:
        return []
    }
  }

  // Load available quizzes from QuizBankService (sử dụng cache)
  useEffect(() => {
    const loadAvailableQuizzes = async () => {
      setLoading(true)
      setScanStatus('Vui lòng chờ trong giây lát')
      console.log('🔄 Loading quizzes from QuizBankService...')



      try {
        // Sử dụng cache nếu có, không force refresh
        const quizzes = await quizBankService.getAllQuizzes()

        console.log(`✅ [TracNghiem] Loaded ${quizzes.length} quizzes`)
        setScanStatus(`Đã tìm thấy ${quizzes.length} bài trắc nghiệm`)
        setAvailableQuizzes(quizzes)
      } catch (error) {
        console.error('❌ Error loading quizzes:', error)
        setScanStatus('Lỗi khi tải trắc nghiệm')
      } finally {
        setLoading(false)
      }
    }

    loadAvailableQuizzes()
  }, [])

  // Filter quizzes using QuizBankService
  useEffect(() => {
    const applyFilters = async () => {
      try {
        const filtered = await quizBankService.filterQuizzes({
          loai: selectedLoai !== 'all' ? (selectedLoai as any) : undefined,
          lop: selectedLop !== 'all' ? parseInt(selectedLop) : undefined,
          bai: selectedBai !== 'all' ? parseInt(selectedBai) : undefined,
          searchTerm: searchTerm || undefined
        })

        console.log(`🔍 Filtered: ${filtered.length} bài trắc nghiệm`)
        setFilteredQuizzes(filtered)
      } catch (error) {
        console.error('❌ Error filtering quizzes:', error)
        setFilteredQuizzes([])
      }
    }

    applyFilters()
  }, [availableQuizzes, selectedLoai, selectedLop, selectedBai, searchTerm])

  // Update URL params
  useEffect(() => {
    const params = new URLSearchParams()
    if (selectedLoai !== 'all') params.set('loai', selectedLoai)
    if (selectedLop !== 'all') params.set('lop', selectedLop)
    if (selectedBai !== 'all') params.set('bai', selectedBai)
    if (searchTerm) params.set('search', searchTerm)

    setSearchParams(params)
  }, [selectedLoai, selectedLop, selectedBai, searchTerm, setSearchParams])

  const getLoaiColor = (loai: string) => {
    switch (loai) {
      case 'BaiHoc': return 'bg-blue-100 text-blue-800'
      case 'KTGKI': return 'bg-green-100 text-green-800'
      case 'KTCKI': return 'bg-yellow-100 text-yellow-800'
      case 'KTGKII': return 'bg-purple-100 text-purple-800'
      case 'KTCKII': return 'bg-orange-100 text-orange-800'
      case 'OnThiTN': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const handleStartQuiz = (quiz: QuizBankItem) => {
    // Navigate to quiz page directly
    console.log(`🎯 Starting quiz: ${quiz.quizUrl}`)
    navigate(quiz.quizUrl)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <Navigation />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center max-w-md mx-auto">
            <div className="relative mb-6">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-t-4 border-blue-600 mx-auto"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="h-10 w-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full opacity-20 animate-pulse"></div>
              </div>
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              Loading database, please wait!
            </h3>
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Navigation />

      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="container mx-auto px-4 py-8"
      >
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-center mb-6">
            <div className="p-4 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full shadow-lg mr-6">
              <ClipboardCheck className="w-12 h-12 text-white" />
            </div>
            <div className="text-left">
              <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">Trắc Nghiệm Vật lí</h1>
              <p className="text-xl text-gray-600 max-w-3xl">
                Luyện tập và kiểm tra kiến thức theo từng nội dung từ Ngân hàng được chọn lọc kĩ càng, đủ các loại câu hỏi theo Chương trình GDPT 2018
              </p>
            </div>
          </div>
        </div>

        {/* Auto exam CTA */}
        <div className="mb-8">
          <div className="rounded-3xl bg-gradient-to-r from-amber-100 via-orange-100 to-orange-200 p-6 shadow-xl flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-sm uppercase tracking-widest font-semibold text-orange-700">Đề luyện tập</p>
              <h2 className="text-2xl font-bold mb-2 text-slate-900">Kiểm tra GKI, CKI, GKII, CKII</h2>
              <p className="text-slate-700">Phân tích với AI sau khi làm bài.</p>
            </div>
            <button
              onClick={() => navigate('/de-tu-dong')}
              className="inline-flex items-center justify-center px-6 py-3 rounded-2xl font-semibold bg-white text-orange-700 hover:bg-orange-50 transition-all duration-300 ease-out shadow-lg border border-orange-200 transform hover:scale-105 focus:scale-105 focus:outline-none focus:ring-4 focus:ring-orange-200 attract-pulse"
            >
              🌟 Bắt đầu
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold text-gray-900">Tìm kiếm và Lọc</h2>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-primary-100 text-primary-600' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-primary-100 text-primary-600' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Tìm kiếm trắc nghiệm..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-4">
            {/* Bộ lọc 1: Loại */}
            <select
              value={selectedLoai}
              onChange={(e) => setSelectedLoai(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">Tất cả loại</option>
              <option value="BaiHoc">Trắc nghiệm bài học</option>
              <option value="KTGKI">Kiểm tra giữa kì I</option>
              <option value="KTCKI">Kiểm tra cuối kì I</option>
              <option value="KTGKII">Kiểm tra giữa kì II</option>
              <option value="KTCKII">Kiểm tra cuối kì II</option>
              <option value="OnThiTN">Ôn thi tốt nghiệp</option>
            </select>

            {/* Bộ lọc 2: Lớp */}
            <select
              value={selectedLop}
              onChange={(e) => {
                setSelectedLop(e.target.value)
                setSelectedBai('all') // Reset bài khi đổi lớp
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">Tất cả lớp</option>
              <option value="10">Lớp 10</option>
              <option value="11">Lớp 11</option>
              <option value="12">Lớp 12</option>
            </select>

            {/* Bộ lọc 3: Bài */}
            <select
              value={selectedBai}
              onChange={(e) => setSelectedBai(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              disabled={selectedLop === 'all'}
            >
              <option value="all">Tất cả bài</option>
              {getBaiListByClass(selectedLop).map(bai => (
                <option key={bai} value={bai}>Bài {bai}</option>
              ))}
            </select>

          </div>
        </div>

        {/* Results */}
        <div className="mb-6">
          <p className="text-gray-600">
            Tìm thấy <span className="font-semibold text-primary-600">{filteredQuizzes.length}</span> bài trắc nghiệm
          </p>
        </div>

        {/* Quiz Items */}
        {filteredQuizzes.length === 0 ? (
          <div className="text-center py-12">
            <Target className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Không tìm thấy trắc nghiệm</h3>
            <p className="text-gray-600">Thử thay đổi bộ lọc hoặc từ khóa tìm kiếm</p>
          </div>
        ) : (
          <div
            className={
              viewMode === 'grid'
                ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
                : 'space-y-4'
            }
          >
            {filteredQuizzes.map((quiz) => (
              <div
                key={quiz.id}
                className="group bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden cursor-pointer"
                onClick={() => handleStartQuiz(quiz)}
              >
                {/* Card Header */}
                <div className="p-4 bg-primary-100 bg-opacity-20">
                  <div className="flex items-center justify-between">
                    <div className="p-2 rounded-lg bg-primary-100">
                      <Target className="w-6 h-6 text-primary-600" />
                    </div>
                    <span className={`px-3 py-1 text-sm font-medium rounded-full ${getLoaiColor(quiz.loai)}`}>
                      {quiz.loaiLabel}
                    </span>
                  </div>
                </div>

                {/* Card Content */}
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                    {quiz.tenBai}
                  </h3>

                  <p className="text-gray-600 mb-4 text-sm">
                    Lớp {quiz.lop} - Chương {quiz.chuong} - Bài {quiz.bai}
                  </p>

                  {/* Question Types - Hiển thị loại câu hỏi có sẵn */}
                  <div className="flex items-center space-x-2 mb-4">
                    {quiz.questionTypes.TN.exists && (
                      <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded font-medium">
                        ✓ TN: {quiz.questionTypes.TN.count} câu
                      </span>
                    )}
                    {quiz.questionTypes.DS.exists && (
                      <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded font-medium">
                        ✓ DS: {quiz.questionTypes.DS.count} câu
                      </span>
                    )}
                    {quiz.questionTypes.TL.exists && (
                      <span className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded font-medium">
                        ✓ TL: {quiz.questionTypes.TL.count} câu
                      </span>
                    )}
                  </div>

                  {/* Metadata */}
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                    <span className="flex items-center space-x-1">
                      <BookOpen className="w-4 h-4" />
                      <span>{quiz.totalQuestions} câu hỏi</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Clock className="w-4 h-4" />
                      <span>~{Math.ceil(quiz.totalQuestions * 1.5)} phút</span>
                    </span>
                  </div>

                  {/* Action Button */}
                  <div className="flex items-center justify-between">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleStartQuiz(quiz)
                      }}
                      className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                    >
                      <Play className="w-4 h-4" />
                      <span>Làm bài</span>
                    </button>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </motion.div>
      <Footer />
    </div>
  )
}

export default TracNghiem
